import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { getPreferredLanguage } from '../../utilities/languagePreference';
import { translateUiText } from '../../utilities/siteTranslations';
import CoinBalanceTrendPanel from './CoinBalanceTrendPanel';
import CoinObservationsPanel from './CoinObservationsPanel';
import {
    cashSalesAssociationState,
    summarizeCashSalesCoverage
} from './cashSalesCoverage';
import {
    buildObservationPresetRange,
    formatObservationDateParam,
    observationRangeDays,
    observationRangeKey,
    observationRangeParams,
    panamaToday
} from './observationDateRange';

const STATUS_OPTIONS = [
    { label: 'Ready', value: 'ready' },
    { label: 'Low', value: 'low' },
    { label: 'Critical', value: 'critical' },
    { label: 'Stale', value: 'stale' },
    { label: 'Data issue', value: 'data_issue' }
];

const STATUS_META = {
    ready: { label: 'Ready', severity: 'success', icon: 'pi pi-check-circle' },
    low: { label: 'Low', severity: 'warning', icon: 'pi pi-exclamation-triangle' },
    critical: { label: 'Critical', severity: 'danger', icon: 'pi pi-times-circle' },
    stale: { label: 'Stale', severity: 'warning', icon: 'pi pi-clock' },
    data_issue: { label: 'Data issue', severity: 'info', icon: 'pi pi-info-circle' }
};

const formatMoney = (value) =>
    Number(value || 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    });

const formatAge = (hours) => {
    if (hours === null || hours === undefined) return 'Unknown';
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m ago`;
    if (hours < 24) return `${Math.round(hours)}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

const toTimestamp = (value) => {
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
};

export const buildMachineDexTimeline = (historyRecords = []) => {
    const readings = (Array.isArray(historyRecords) ? historyRecords : [])
        .filter((record) => toTimestamp(record?.observed_at) !== null)
        .sort((left, right) => toTimestamp(left.observed_at) - toTimestamp(right.observed_at));
    const readingEvents = readings.map((record) => ({
        type: 'reading',
        timestamp: record.observed_at,
        record
    }));
    const cashSaleEvents = readings
        .filter((record) => {
            const amount = Number(record?.sales_context?.cash_sales);
            return Number.isFinite(amount) && amount > 0 &&
                record?.sales_context?.confidence !== 'ambiguous';
        })
        .map((record) => ({
            type: 'cash_sale',
            timestamp: record.sales_context.dex_reported_at || record.observed_at,
            sale: {
                cashAmount: Number(record.sales_context.cash_sales),
                confidence: record.sales_context.confidence
            }
        }));

    return [...readingEvents, ...cashSaleEvents].sort(
        (left, right) => toTimestamp(left.timestamp) - toTimestamp(right.timestamp)
    );
};

const formatSignedMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    if (amount === 0) return formatMoney(0);
    return `${amount > 0 ? '+' : '-'}${formatMoney(Math.abs(amount))}`;
};

const escapeCsvCell = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value);
    return `"${normalized.replace(/"/g, '""')}"`;
};

const ParlevelCoinTubes = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const machineTableRef = useRef(null);
    const historyRequestsRef = useRef(new Set());
    const machineHistoryRequestsRef = useRef(new Set());
    const postServiceRangeRef = useRef(null);
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const appT = useCallback(
        (value) => translateUiText(value, preferredLanguage),
        [preferredLanguage]
    );
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [rangeDashboard, setRangeDashboard] = useState(null);
    const [observationData, setObservationData] = useState(null);
    const [reviewObservationData, setReviewObservationData] = useState(null);
    const [postServiceObservationData, setPostServiceObservationData] = useState(null);
    const [postServiceLoading, setPostServiceLoading] = useState(false);
    const [observationHistory, setObservationHistory] = useState({});
    const [machineHistory, setMachineHistory] = useState({});
    const [observationLoading, setObservationLoading] = useState(false);
    const [observationMode, setObservationMode] = useState('balance');
    const [observationDateRange, setObservationDateRange] = useState(
        () => buildObservationPresetRange('30')
    );
    const [draftDateRange, setDraftDateRange] = useState(
        () => buildObservationPresetRange('30')
    );
    const [rangePreset, setRangePreset] = useState('30');
    const [dateRangeError, setDateRangeError] = useState('');
    const [openSections, setOpenSections] = useState({
        summary: true,
        trend: true,
        machines: true,
        routes: true
    });
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [expandedRows, setExpandedRows] = useState(null);
    const [search, setSearch] = useState('');
    const [route, setRoute] = useState(null);
    const [status, setStatus] = useState(null);
    const [showActionOnly, setShowActionOnly] = useState(false);
    const observationRangeQuery = useMemo(
        () => observationRangeParams(observationDateRange),
        [observationDateRange]
    );
    const currentObservationRangeKey = useMemo(
        () => observationRangeKey(observationDateRange),
        [observationDateRange]
    );

    useEffect(() => {
        const sync = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('preferred-language-changed', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const fetchData = useCallback(async () => {
        if (!observationRangeQuery) return;
        setLoading(true);
        try {
            const response = await handleGetRequest(
                endPoints.parlevelChangeLevelRangeDashboard(observationRangeQuery),
                false,
                false,
                dispatch
            );
            const dashboard = response?.data || response || null;
            setRangeDashboard(dashboard);
            setRows(Array.isArray(dashboard?.snapshot?.rows) ? dashboard.snapshot.rows : []);
            setSummary(dashboard?.snapshot?.summary || null);
        } catch {
            toast.error(appT('Failed to load coin tube data'));
        }
        setLoading(false);
    }, [appT, dispatch, observationRangeQuery]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const applyPageDateRange = (nextRange) => {
        const days = observationRangeDays(nextRange);
        if (!days) return;
        if (days > 93) {
            setDateRangeError(appT('Maximum observation range is 93 days.'));
            return;
        }
        setDateRangeError('');
        setObservationDateRange(nextRange);
    };

    const toggleSection = (section) => {
        setOpenSections((current) => ({ ...current, [section]: !current[section] }));
    };

    const fetchObservationData = useCallback(async () => {
        if (!observationRangeQuery) return;
        setObservationLoading(true);
        try {
            const [observationResponse, reviewObservationResponse] = await Promise.all([
                handleGetRequest(
                    endPoints.parlevelChangeLevelObservations(
                        `${observationRangeQuery}&limit=500`
                    ),
                    false,
                    false,
                    dispatch
                ),
                handleGetRequest(
                    endPoints.parlevelChangeLevelObservations(
                        `${observationRangeQuery}&status=review&limit=500`
                    ),
                    false,
                    false,
                    dispatch
                )
            ]);
            setObservationData(observationResponse?.data || observationResponse || null);
            setReviewObservationData(
                reviewObservationResponse?.data || reviewObservationResponse || null
            );
        } catch {
            toast.error(appT('Failed to load coin observations'));
        }
        setObservationLoading(false);
    }, [appT, dispatch, observationRangeQuery]);

    useEffect(() => {
        setPostServiceObservationData(null);
        postServiceRangeRef.current = null;
        setObservationHistory({});
        historyRequestsRef.current.clear();
        setMachineHistory({});
        machineHistoryRequestsRef.current.clear();
        setExpandedRows(null);
        fetchObservationData();
    }, [currentObservationRangeKey, fetchObservationData]);

    const fetchPostServiceObservations = useCallback(async () => {
        if (
            !observationRangeQuery ||
            postServiceLoading ||
            postServiceRangeRef.current === currentObservationRangeKey
        ) return;
        postServiceRangeRef.current = currentObservationRangeKey;
        setPostServiceLoading(true);
        try {
            const response = await handleGetRequest(
                endPoints.parlevelPostServiceObservations(
                    `${observationRangeQuery}&min_drop_value=0.01&max_after_minutes=15&limit=2000`
                ),
                false,
                false,
                dispatch
            );
            setPostServiceObservationData(response?.data || response || null);
        } catch {
            postServiceRangeRef.current = null;
            toast.error(appT('Failed to load post-service observations'));
        }
        setPostServiceLoading(false);
    }, [
        appT,
        currentObservationRangeKey,
        dispatch,
        observationRangeQuery,
        postServiceLoading
    ]);

    useEffect(() => {
        if (observationMode === 'post_service') {
            fetchPostServiceObservations();
        }
    }, [
        currentObservationRangeKey,
        fetchPostServiceObservations,
        observationMode
    ]);

    const routeOptions = useMemo(
        () => [...new Set(rows.map((row) => row.route).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b))
            .map((value) => ({ label: value, value })),
        [rows]
    );

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter((row) => {
            if (route && row.route !== route) return false;
            if (status && row.operational_status !== status) return false;
            if (showActionOnly && row.operational_status === 'ready') return false;
            if (!needle) return true;
            return [row.machine_name, row.location, row.route, row.asset, row.system_id]
                .some((value) => String(value || '').toLowerCase().includes(needle));
        });
    }, [rows, route, search, showActionOnly, status]);

    const exportFilteredRows = () => {
        if (!filteredRows.length) {
            toast.info(appT('No filtered records to export'));
            return;
        }

        const headers = [
            appT('Machine ID'),
            appT('Machine'),
            appT('Location'),
            appT('Route'),
            appT('Status'),
            appT('Total value'),
            appT('Coins'),
            appT('Freshness'),
            appT('Reading source'),
            appT('Last update'),
            appT('Quality'),
            appT('Tubes')
        ];
        const lines = filteredRows.map((row) => {
            const statusMeta = STATUS_META[row.operational_status] || STATUS_META.data_issue;
            const lastUpdate =
                row.source_mode === 'current'
                    ? row.fetch_date || row.reported_at
                    : row.reported_at || row.fetch_date;
            const tubeDetails = (row.tubes || [])
                .map(
                    (tube) =>
                        `${tube.tube}: ${tube.count} x ${formatMoney(tube.denomination)} = ${formatMoney(tube.total)}`
                )
                .join(' | ');

            return [
                row.system_id,
                row.machine_name,
                row.location,
                row.route,
                appT(statusMeta.label),
                Number(row.total_value || 0).toFixed(2),
                row.total_coin_count || 0,
                formatAge(row.age_hours),
                row.source_mode === 'current' ? appT('Current reading') : appT('Last reported'),
                lastUpdate ? new Date(lastUpdate).toISOString() : '',
                row.quality_status,
                tubeDetails
            ]
                .map(escapeCsvCell)
                .join(',');
        });
        const csv = `\uFEFF${headers.map(escapeCsvCell).join(',')}\n${lines.join('\n')}`;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `parlevel-coin-tubes-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        toast.success(`${filteredRows.length} ${appT('records exported')}`);
    };

    const runRefresh = () => {
        confirmDialog({
            header: appT('Confirm Change Level Run'),
            message: appT('Retrieve fresh coin tube levels for all connected Parlevel machines?'),
            icon: 'pi pi-refresh',
            accept: async () => {
                setRunning(true);
                try {
                    const response = await dispatch(
                        handlePostRequest({}, endPoints.parlevelRunChangeLevels, true, true)
                    );
                    if (response?.status === 200 || response?.status === 201) {
                        toast.success(appT('Coin tube levels updated'));
                        await fetchData();
                    }
                } catch {
                    toast.error(appT('Change level run failed'));
                }
                setRunning(false);
            }
        });
    };

    const statusBody = (row) => {
        const meta = STATUS_META[row.operational_status] || STATUS_META.data_issue;
        return <Tag value={appT(meta.label)} severity={meta.severity} icon={meta.icon} />;
    };

    const machineBody = (row) => (
        <div>
            <div className="font-semibold">{row.machine_name || appT('Unnamed Machine')}</div>
            <small className="text-color-secondary">
                {row.location || '-'} · ID {row.system_id}
            </small>
        </div>
    );

    const freshnessBody = (row) => (
        <div>
            <div>{formatAge(row.age_hours)}</div>
            <small className="text-color-secondary">
                {row.source_mode === 'current' ? appT('Current reading') : appT('Last reported')}
            </small>
        </div>
    );

    const tubeSummaryBody = (row) => (
        <div className="flex flex-wrap gap-1">
            {(row.tubes || []).slice(0, 6).map((tube) => (
                <Tag
                    key={`${row.system_id}-${tube.tube}`}
                    value={`${String(tube.tube || '').replace('TUBE_', '')}: ${tube.count}`}
                    severity={tube.count === 0 ? 'danger' : tube.count < 20 ? 'warning' : 'info'}
                    title={`${String(tube.tube || '').replace('_', ' ')} · ${formatMoney(tube.denomination)} ${appT('each')} · ${tube.count} ${appT('coins')} · ${formatMoney(tube.total)}`}
                />
            ))}
        </div>
    );

    const filterByRoute = (selectedRoute) => {
        setRoute(selectedRoute);
        machineTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const focusObservationMachine = (observation) => {
        setSearch(String(observation.system_id || observation.machine_name || ''));
        setRoute(null);
        setStatus(null);
        setShowActionOnly(false);
        machineTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const loadObservationHistory = useCallback(
        async (observation) => {
            const systemId = Number(observation?.system_id);
            const historyKey = observation?.observation_key || String(systemId);
            const observedAt = new Date(
                observation?.observed_at || observation?.event_at
            );
            if (
                !systemId ||
                Number.isNaN(observedAt.getTime()) ||
                historyRequestsRef.current.has(historyKey) ||
                observationHistory[historyKey]?.loaded
            ) return;

            const from = new Date(observedAt);
            from.setDate(from.getDate() - 1);
            const to = new Date(observedAt);
            to.setDate(to.getDate() + 2);
            const historyParams = [
                `from=${formatObservationDateParam(from)}`,
                `to=${formatObservationDateParam(to)}`,
                'limit=5000'
            ].join('&');

            historyRequestsRef.current.add(historyKey);
            setObservationHistory((current) => ({
                ...current,
                [historyKey]: {
                    loading: true,
                    loaded: false,
                    error: null,
                    records: []
                }
            }));

            try {
                const response = await handleGetRequest(
                    endPoints.parlevelChangeLevelHistory(systemId, historyParams),
                    false,
                    false,
                    dispatch
                );
                const records = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                        ? response
                        : [];
                setObservationHistory((current) => ({
                    ...current,
                    [historyKey]: {
                        loading: false,
                        loaded: true,
                        error: null,
                        records
                    }
                }));
            } catch {
                setObservationHistory((current) => ({
                    ...current,
                    [historyKey]: {
                        loading: false,
                        loaded: false,
                        error: appT('Could not load the detailed history'),
                        records: []
                    }
                }));
            } finally {
                historyRequestsRef.current.delete(historyKey);
            }
        },
        [appT, dispatch, observationHistory]
    );

    const routeReadinessBody = (row) => {
        const readiness = Number(row.readiness_percent || 0);
        const severity = readiness >= 80 ? 'success' : readiness >= 60 ? 'warning' : 'danger';
        return <Tag value={`${readiness.toFixed(1)}%`} severity={severity} />;
    };

    const loadMachineHistory = useCallback(
        async (row) => {
            const systemId = Number(row?.system_id);
            const historyKey = String(systemId);
            if (
                !systemId ||
                !observationRangeQuery ||
                machineHistoryRequestsRef.current.has(historyKey) ||
                machineHistory[historyKey]?.loaded
            ) return;

            machineHistoryRequestsRef.current.add(historyKey);
            setMachineHistory((current) => ({
                ...current,
                [historyKey]: { loading: true, loaded: false, error: null, records: [] }
            }));

            try {
                const response = await handleGetRequest(
                    endPoints.parlevelChangeLevelHistory(
                        systemId,
                        `${observationRangeQuery}&limit=5000`
                    ),
                    false,
                    false,
                    dispatch
                );
                const records = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                        ? response
                        : [];
                setMachineHistory((current) => ({
                    ...current,
                    [historyKey]: { loading: false, loaded: true, error: null, records }
                }));
            } catch {
                setMachineHistory((current) => ({
                    ...current,
                    [historyKey]: {
                        loading: false,
                        loaded: false,
                        error: appT('Could not load machine history'),
                        records: []
                    }
                }));
            } finally {
                machineHistoryRequestsRef.current.delete(historyKey);
            }
        },
        [appT, dispatch, machineHistory, observationRangeQuery]
    );

    const formatDateTime = (value) => {
        if (!value) return '-';
        return new Date(value).toLocaleString(
            preferredLanguage === 'es' ? 'es-PA' : 'en-US',
            {
                timeZone: 'America/Panama',
                dateStyle: 'medium',
                timeStyle: 'short'
            }
        );
    };

    const tubeHistoryDetail = (record) => {
        const tubes = (record.tubes || [])
            .map((tube) => `${String(tube.tube || '').replace('TUBE_', '')}: ${tube.count}`)
            .join(' · ');
        const changes = (record.tube_deltas || [])
            .filter((tube) => Number(tube.delta_count || 0) !== 0)
            .map((tube) => {
                const count = Number(tube.delta_count || 0);
                return `${String(tube.tube || '').replace('TUBE_', '')}: ${count > 0 ? '+' : ''}${count}`;
            })
            .join(' · ');
        return (
            <div>
                <div>{tubes || '-'}</div>
                {changes && (
                    <small className="text-color-secondary">
                        {appT('Tube changes')}: {changes}
                    </small>
                )}
            </div>
        );
    };

    const expansionTemplate = (row) => {
        const historyKey = String(row.system_id);
        const historyState = machineHistory[historyKey] || {
            loading: false,
            loaded: false,
            error: null,
            records: []
        };
        const timeline = buildMachineDexTimeline(historyState.records);
        const salesCoverage = summarizeCashSalesCoverage(
            timeline.filter((event) => event.type === 'reading').map((event) => event.record)
        );

        return (
        <div className="p-3 surface-50">
            <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                <div>
                    <div className="font-semibold text-lg">{appT('Coin tubes')}</div>
                    <small className="text-color-secondary">
                        {appT('Normalized from')} {row.source_mode === 'current' ? appT('current reading') : appT('last reported reading')}
                    </small>
                </div>
                {row.quality_status !== 'good' && (
                    <Tag
                        value={`${appT('Quality')}: ${appT(row.quality_status)}`}
                        severity={row.quality_status === 'invalid' ? 'danger' : 'warning'}
                    />
                )}
            </div>
            <div className="grid">
                {(row.tubes || []).map((tube) => (
                    <div className="col-12 sm:col-6 md:col-4 lg:col-3" key={`${row.system_id}-${tube.tube}`}>
                        <Card className="h-full">
                            <div className="flex justify-content-between align-items-center mb-2">
                                <span className="font-semibold">{String(tube.tube || '').replace('_', ' ')}</span>
                                {tube.full && <Tag value={appT('Full')} severity="success" />}
                            </div>
                            <div className="text-2xl font-bold">{tube.count} <span className="text-sm font-normal">{appT('coins')}</span></div>
                            <div className="text-color-secondary">
                                {tube.denomination == null ? appT('Unknown denomination') : `${formatMoney(tube.denomination)} ${appT('each')}`}
                            </div>
                            <div className="mt-2 font-semibold">{formatMoney(tube.total)}</div>
                        </Card>
                    </div>
                ))}
            </div>
            {(row.quality_issues || []).length > 0 && (
                <small className="text-orange-700">
                    <i className="pi pi-info-circle mr-1" />
                    {appT('Data notes')}: {row.quality_issues.join(', ')}
                </small>
            )}

            <div className="mt-4 pt-3 border-top-1 surface-border">
                <div className="flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                    <div>
                        <div className="font-semibold text-lg">{appT('DEX and cash sales history')}</div>
                        <small className="text-color-secondary">
                            {appT('Cash sales associated with each DEX report in the selected period')}
                        </small>
                    </div>
                    <small className="text-color-secondary">
                        <i className="pi pi-info-circle mr-1" />
                        {appT('The sale does not confirm how the coin tubes changed.')}
                    </small>
                </div>

                {historyState.loaded && salesCoverage.total > 0 && (
                    <div className="flex flex-wrap gap-3 text-sm mb-2">
                        <span className="text-green-700">
                            <i className="pi pi-check-circle mr-1" />
                            {appT('Cash sales associated')}: {salesCoverage.associated} / {salesCoverage.total} ({salesCoverage.percent}%)
                        </span>
                        {salesCoverage.missing > 0 && (
                            <span className="text-orange-700">
                                {appT('Sales data not associated')}: {salesCoverage.missing}
                            </span>
                        )}
                        {salesCoverage.ambiguous > 0 && (
                            <span className="text-orange-700">
                                {appT('Ambiguous sales data')}: {salesCoverage.ambiguous}
                            </span>
                        )}
                    </div>
                )}

                {historyState.loading ? (
                    <div className="flex align-items-center justify-content-center gap-2 py-4 text-color-secondary">
                        <i className="pi pi-spin pi-spinner" />
                        {appT('Loading machine history')}
                    </div>
                ) : historyState.error ? (
                    <div className="p-3 border-round surface-100 text-orange-700 flex justify-content-between align-items-center gap-2">
                        <span><i className="pi pi-exclamation-triangle mr-2" />{historyState.error}</span>
                        <Button
                            label={appT('Try again')}
                            icon="pi pi-refresh"
                            className="p-button-outlined p-button-sm"
                            onClick={() => loadMachineHistory(row)}
                        />
                    </div>
                ) : timeline.length ? (
                    <div className="overflow-auto">
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse', minWidth: '900px' }}>
                            <thead>
                                <tr className="border-bottom-1 surface-border text-left">
                                    <th className="p-2">{appT('Date and time')}</th>
                                    <th className="p-2">{appT('Event')}</th>
                                    <th className="p-2">{appT('Coin balance')}</th>
                                    <th className="p-2">{appT('Variation')}</th>
                                    <th className="p-2">{appT('Coins')}</th>
                                    <th className="p-2">{appT('Tubes and changes')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {timeline.map((event, index) => {
                                    if (event.type === 'cash_sale') {
                                        return (
                                            <tr className="border-bottom-1 surface-border bg-blue-50" key={`cash-sale-${event.timestamp}-${index}`}>
                                                <td className="p-2">{formatDateTime(event.timestamp)}</td>
                                                <td className="p-2">
                                                    <Tag value={appT('Cash sale')} severity="info" icon="pi pi-dollar" />
                                                    <span className="ml-2 font-semibold">{formatMoney(event.sale.cashAmount)}</span>
                                                    <small className="ml-2 text-color-secondary">
                                                        DEX · {appT(event.sale.confidence === 'hourly' ? 'Hourly association' : 'Exact association')}
                                                    </small>
                                                </td>
                                                <td className="p-2 text-color-secondary">-</td>
                                                <td className="p-2 text-color-secondary">-</td>
                                                <td className="p-2 text-color-secondary">-</td>
                                                <td className="p-2 text-color-secondary">
                                                    {appT('The sale does not confirm how the coin tubes changed.')}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    const record = event.record;
                                    const salesState = cashSalesAssociationState(record);
                                    return (
                                        <tr className="border-bottom-1 surface-border" key={`reading-${record.observed_at}-${index}`}>
                                            <td className="p-2">{formatDateTime(record.observed_at)}</td>
                                            <td className="p-2">
                                                <Tag
                                                    value={appT(Number(record.delta_value || 0) === 0 ? 'Reading' : 'Changed reading')}
                                                    severity={Number(record.delta_value || 0) === 0 ? 'info' : 'warning'}
                                                />
                                                {salesState === 'no_sale' && (
                                                    <small className="block mt-1 text-color-secondary">
                                                        {appT('No cash sale')}
                                                    </small>
                                                )}
                                                {salesState === 'missing' && (
                                                    <small className="block mt-1 text-orange-700">
                                                        {appT('Sales data not associated')}
                                                    </small>
                                                )}
                                                {salesState === 'ambiguous' && (
                                                    <small className="block mt-1 text-orange-700">
                                                        {appT('Ambiguous sales data')}
                                                    </small>
                                                )}
                                            </td>
                                            <td className="p-2 font-semibold">{formatMoney(record.total_value)}</td>
                                            <td className={`p-2 font-semibold ${Number(record.delta_value) < 0 ? 'text-orange-600' : Number(record.delta_value) > 0 ? 'text-green-600' : ''}`}>
                                                {record.delta_value === null || record.delta_value === undefined
                                                    ? '-'
                                                    : formatSignedMoney(record.delta_value)}
                                            </td>
                                            <td className="p-2">
                                                {Number(record.total_coin_count || 0).toLocaleString()}
                                                {record.delta_coin_count !== null && record.delta_coin_count !== undefined && (
                                                    <small className="block text-color-secondary">
                                                        {Number(record.delta_coin_count) > 0 ? '+' : ''}{Number(record.delta_coin_count)}
                                                    </small>
                                                )}
                                            </td>
                                            <td className="p-2">{tubeHistoryDetail(record)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-3 border-round surface-100 text-color-secondary">
                        {historyState.loaded
                            ? appT('No DEX readings were found for this period')
                            : appT('Open the machine to load its DEX history')}
                    </div>
                )}
            </div>
        </div>
        );
    };

    const statusCount = (key) => Number(summary?.by_status?.[key] || 0);
    const attentionCount =
        statusCount('low') + statusCount('critical') + statusCount('stale') + statusCount('data_issue');

    return (
        <div className="grid">
            <ConfirmDialog />
            <div className="col-12 flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                    <h2 className="m-0">
                        <i className="pi pi-dollar mr-2" />
                        {appT('Coin Tubes')}
                    </h2>
                    <p className="text-color-secondary mt-1 mb-0">
                        {appT('Change availability by machine and route')}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button label={appT('Overview')} icon="pi pi-chart-line" className="p-button-outlined p-button-sm" onClick={() => navigate('/parlevel')} />
                    <Button label={appT('Data & Sync')} icon="pi pi-database" className="p-button-outlined p-button-sm" onClick={() => navigate('/parlevel/data')} />
                    <Button
                        label={appT('Export CSV')}
                        icon="pi pi-download"
                        className="p-button-outlined p-button-sm"
                        disabled={!filteredRows.length}
                        onClick={exportFilteredRows}
                    />
                    <Button label={appT('Refresh Coin Tubes')} icon="pi pi-refresh" className="p-button-sm" loading={running} onClick={runRefresh} />
                </div>
            </div>

            <div className="col-12">
                <Card>
                    <div className="grid align-items-end">
                        <div className="col-12 md:col-3">
                            <label className="block font-semibold text-sm mb-2">{appT('Period')}</label>
                            <Dropdown
                                value={rangePreset}
                                options={[
                                    { label: appT('Last 30 days'), value: '30' },
                                    { label: appT('Last 90 days'), value: '90' },
                                    { label: appT('This month'), value: 'month' },
                                    { label: appT('Custom range'), value: 'custom' }
                                ]}
                                onChange={(event) => {
                                    setRangePreset(event.value);
                                    if (event.value === 'custom') return;
                                    const nextRange = buildObservationPresetRange(event.value);
                                    setDraftDateRange(nextRange);
                                    applyPageDateRange(nextRange);
                                }}
                                className="w-full"
                            />
                        </div>
                        <div className="col-12 md:col-5">
                            <label className="block font-semibold text-sm mb-2">{appT('Date range')}</label>
                            <Calendar
                                value={draftDateRange}
                                selectionMode="range"
                                readOnlyInput
                                showIcon
                                maxDate={panamaToday()}
                                dateFormat="dd/mm/yy"
                                className="w-full"
                                onChange={(event) => {
                                    const nextRange = event.value || [];
                                    setDraftDateRange(nextRange);
                                    setRangePreset('custom');
                                    if (nextRange?.[0] && nextRange?.[1]) applyPageDateRange(nextRange);
                                }}
                            />
                            {dateRangeError && <small className="block text-red-500 mt-1">{dateRangeError}</small>}
                        </div>
                        <div className="col-12 md:col-4">
                            <small className="text-color-secondary">
                                {appT('This date range updates every section on the page. Select up to 93 days.')}
                            </small>
                        </div>
                    </div>
                </Card>
            </div>

            {loading && !summary ? (
                <div className="col-12 flex justify-content-center py-6">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                </div>
            ) : (
                <>
                    <div className="col-12">
                        <Card>
                            <div className="flex justify-content-between align-items-center gap-2">
                                <div>
                                    <div className="text-xl font-semibold">{appT('Operational summary')}</div>
                                    <small className="text-color-secondary">{appT('Snapshot at the end of the selected period')}</small>
                                </div>
                                <Button
                                    label={openSections.summary ? appT('Hide summary') : appT('View summary')}
                                    icon={openSections.summary ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => toggleSection('summary')}
                                />
                            </div>
                        </Card>
                    </div>
                    {openSections.summary && (
                        <>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-color-secondary text-sm">{appT('Reporting')}</div>
                            <div className="text-3xl font-bold text-primary">{summary?.reporting_machines || 0}</div>
                            <small>{summary?.checked_machines || 0} {appT('machines checked')}</small>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-color-secondary text-sm">{appT('Ready')}</div>
                            <div className="text-3xl font-bold text-green-600">{statusCount('ready')}</div>
                            <small>{appT('Valid and sufficiently funded')}</small>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-color-secondary text-sm">{appT('Coin attention')}</div>
                            <div className="text-3xl font-bold text-orange-500">{attentionCount}</div>
                            <small>
                                {appT('Among reporting machines')} · {summary?.no_data_machines || 0} {appT('without tube data')}
                            </small>
                        </Card>
                    </div>
                    <div className="col-6 md:col-3">
                        <Card className="h-full">
                            <div className="text-color-secondary text-sm">{appT('Change float')}</div>
                            <div className="text-3xl font-bold">{formatMoney(summary?.total_value)}</div>
                            <small>{Number(summary?.total_coin_count || 0).toLocaleString()} {appT('coins')}</small>
                        </Card>
                    </div>
                        </>
                    )}

                    <div className="col-12">
                        <Card>
                            <div className="flex justify-content-between align-items-center gap-2 mb-3">
                                <div>
                                    <div className="text-xl font-semibold">
                                        <i className="pi pi-chart-line mr-2 text-primary" />
                                        {appT('Change float evolution')}
                                    </div>
                                    <small className="text-color-secondary">
                                        {appT('Fleet-adjusted balance, reporting coverage, installations and removals')}
                                    </small>
                                </div>
                                <Button
                                    label={openSections.trend ? appT('Hide trend') : appT('View trend')}
                                    icon={openSections.trend ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => toggleSection('trend')}
                                />
                            </div>
                            {openSections.trend && (
                                <CoinBalanceTrendPanel data={rangeDashboard} loading={loading} appT={appT} />
                            )}
                        </Card>
                    </div>

                    <div className="col-12">
                        <CoinObservationsPanel
                            data={observationData}
                            reviewData={reviewObservationData}
                            postServiceData={postServiceObservationData}
                            postServiceLoading={postServiceLoading}
                            onLoadPostService={fetchPostServiceObservations}
                            loading={observationLoading}
                            analysisMode={observationMode}
                            onAnalysisModeChange={setObservationMode}
                            appT={appT}
                            preferredLanguage={preferredLanguage}
                            onFocusMachine={focusObservationMachine}
                            historyBySystemId={observationHistory}
                            onLoadHistory={loadObservationHistory}
                        />
                    </div>

                    <div className="col-12">
                        <Card>
                            <div className="flex justify-content-between align-items-center gap-2">
                                <div>
                                    <div className="text-xl font-semibold">{appT('Machine coin availability')}</div>
                                    <small className="text-color-secondary">{appT('Latest reading within the selected period')}</small>
                                </div>
                                <Button
                                    label={openSections.machines ? appT('Hide machines') : appT('View machines')}
                                    icon={openSections.machines ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => toggleSection('machines')}
                                />
                            </div>
                        </Card>
                    </div>

                    {openSections.machines && (
                        <>
                    <div className="col-12">
                        <Card>
                            <div className="grid align-items-end">
                                <div className="col-12 md:col-4">
                                    <label className="block font-semibold text-sm mb-2">{appT('Search')}</label>
                                    <span className="p-input-icon-left w-full">
                                        <i className="pi pi-search" />
                                        <InputText
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder={appT('Machine, location, route, asset or ID')}
                                            className="w-full"
                                        />
                                    </span>
                                </div>
                                <div className="col-12 md:col-2">
                                    <label className="block font-semibold text-sm mb-2">{appT('Route')}</label>
                                    <Dropdown value={route} options={routeOptions} onChange={(event) => setRoute(event.value)} placeholder={appT('All Routes')} filter showClear className="w-full" />
                                </div>
                                <div className="col-12 md:col-2">
                                    <label className="block font-semibold text-sm mb-2">{appT('Status')}</label>
                                    <Dropdown
                                        value={status}
                                        options={STATUS_OPTIONS.map((option) => ({ ...option, label: appT(option.label) }))}
                                        onChange={(event) => setStatus(event.value)}
                                        placeholder={appT('All coin tube statuses')}
                                        showClear
                                        className="w-full"
                                    />
                                </div>
                                <div className="col-12 md:col-3">
                                    <label className="block font-semibold text-sm mb-2" htmlFor="coin-tubes-action-only">
                                        {appT('Action required only')}
                                    </label>
                                    <div className="flex align-items-center gap-2" style={{ minHeight: '2.5rem' }}>
                                        <InputSwitch
                                            inputId="coin-tubes-action-only"
                                            checked={showActionOnly}
                                            onChange={(event) => {
                                                setShowActionOnly(event.value);
                                                if (event.value && status === 'ready') setStatus(null);
                                            }}
                                        />
                                        <span className="text-color-secondary">
                                            {showActionOnly ? attentionCount : appT('All reporting machines')}
                                        </span>
                                    </div>
                                </div>
                                <div className="col-12 md:col-1">
                                    <Button
                                        icon="pi pi-filter-slash"
                                        className="p-button-outlined w-full"
                                        tooltip={appT('Clear Filters')}
                                        onClick={() => {
                                            setSearch('');
                                            setRoute(null);
                                            setStatus(null);
                                            setShowActionOnly(false);
                                        }}
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-12" ref={machineTableRef}>
                        <Card>
                            <DataTable
                                value={filteredRows}
                                loading={loading}
                                expandedRows={expandedRows}
                                onRowToggle={(event) => setExpandedRows(event.data)}
                                onRowExpand={(event) => loadMachineHistory(event.data)}
                                rowExpansionTemplate={expansionTemplate}
                                dataKey="system_id"
                                paginator
                                rows={20}
                                rowsPerPageOptions={[20, 50, 100]}
                                sortField="operational_status"
                                sortOrder={1}
                                emptyMessage={appT('No coin tube records found')}
                                responsiveLayout="scroll"
                                size="small"
                            >
                                <Column expander style={{ width: '3rem' }} />
                                <Column header={appT('Status')} body={statusBody} sortable field="operational_status" style={{ minWidth: '125px' }} />
                                <Column header={appT('Machine')} body={machineBody} sortable field="machine_name" style={{ minWidth: '240px' }} />
                                <Column header={appT('Route')} field="route" sortable style={{ minWidth: '140px' }} />
                                <Column header={appT('Total value')} body={(row) => <span className="font-semibold">{formatMoney(row.total_value)}</span>} sortable field="total_value" />
                                <Column header={appT('Coins')} field="total_coin_count" sortable />
                                <Column header={appT('Tubes')} body={tubeSummaryBody} style={{ minWidth: '260px' }} />
                                <Column header={appT('Freshness')} body={freshnessBody} sortable field="age_hours" style={{ minWidth: '140px' }} />
                            </DataTable>
                        </Card>
                    </div>
                        </>
                    )}

                    <div className="col-12">
                        <Card>
                            <div className="flex justify-content-between align-items-center gap-2">
                                <div>
                                    <div className="text-xl font-semibold">{appT('Route readiness')}</div>
                                    <small className="text-color-secondary">{appT('Route preparation at the end of the selected period')}</small>
                                </div>
                                <Button
                                    label={openSections.routes ? appT('Hide routes') : appT('View routes')}
                                    icon={openSections.routes ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
                                    className="p-button-outlined p-button-sm"
                                    onClick={() => toggleSection('routes')}
                                />
                            </div>
                            {openSections.routes && (
                            <DataTable value={summary?.routes || []} paginator rows={10} size="small" responsiveLayout="scroll" className="mt-3">
                                <Column
                                    field="route"
                                    header={appT('Route')}
                                    sortable
                                    body={(row) => (
                                        <Button
                                            label={row.route}
                                            className="p-button-link p-0"
                                            onClick={() => filterByRoute(row.route)}
                                        />
                                    )}
                                />
                                <Column field="total_machines" header={appT('Total machines')} sortable />
                                <Column field="reporting" header={appT('Reporting')} sortable />
                                <Column field="ready" header={appT('Ready')} sortable />
                                <Column field="attention" header={appT('Coin attention')} sortable body={(row) => <Tag value={row.attention} severity={row.attention ? 'warning' : 'success'} />} />
                                <Column field="no_data" header={appT('No data')} sortable body={(row) => <Tag value={row.no_data} severity={row.no_data ? 'danger' : 'success'} />} />
                                <Column field="readiness_percent" header={appT('Readiness')} sortable body={routeReadinessBody} />
                                <Column field="total_value" header={appT('Change float')} sortable body={(row) => formatMoney(row.total_value)} />
                            </DataTable>
                            )}
                        </Card>
                    </div>
                </>
            )}
        </div>
    );
};

export default ParlevelCoinTubes;
